import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Info,
  Plus,
  VButton,
  VFlex,
  VInput,
  VStack,
  VTextButton,
  VTooltip,
} from '@/package';
import { useVToast } from '@/package';
import type { User } from '@/type';

import type { TaskCreateFormType } from '../TaskCreateForm/type';
import {
  type MemberTaskBaseRow,
  MemberTaskBaseTable,
} from './MemberTaskBaseTable';

type MemberAllocateRatioTableProps = {
  readOnly?: boolean;
  onAddUser?: () => void;
};

export const MemberAllocateRatioTable = ({
  readOnly,
  onAddUser,
}: MemberAllocateRatioTableProps) => {
  const { t } = useTranslation();
  const { addToast } = useVToast();
  const { formState, register, setValue, watch } =
    useFormContext<TaskCreateFormType>();
  const userList = watch('worker') ?? [];
  const [rowList, setRowList] = useState<
    MemberTaskBaseRow<{ '7dCompleted': number }>[]
  >([]);
  const taskRatioByUserIDRef = useRef<{ [userID: User['id']]: number }>({});

  const setTotalTaskRatio = (totalTaskRatio: number) => {
    setValue('totalTaskRatio', Number(totalTaskRatio.toFixed(2)), {
      shouldTouch: true,
      shouldValidate: true,
    });
    refreshDistributions();
  };

  const refreshDistributions = () => {
    const distributions = Object.entries(taskRatioByUserIDRef.current).map(
      ([userID, allocateRatio]) => {
        return {
          userID: userID as string,
          allocateRatio,
        };
      },
    );

    setValue('distribution.distributions', distributions, {
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const calculateTotalTaskRatio = () => {
    let total = 0;

    Object.values(taskRatioByUserIDRef.current).forEach(taskRatio => {
      total += taskRatio;
    });

    setTotalTaskRatio(total);
  };

  const redistribute = (evenly = false) => {
    let total = 100;

    setTotalTaskRatio(total);

    const rowList = userList.map((user, index) => {
      let taskRatio = 0;

      if (evenly || taskRatioByUserIDRef.current[user.id] == null) {
        taskRatio = Number(((1 / userList.length) * 100).toFixed(0));

        if (index === userList.length - 1) {
          taskRatio = total;
        }

        total -= taskRatio;

        taskRatioByUserIDRef.current[user.id] = taskRatio;
      } else {
        taskRatio = taskRatioByUserIDRef.current[user.id];
      }

      return {
        id: user.id,
        name: user.name || user.email,
        taskRatio,
        '7dCompleted': user.recentCompleted,
      } as MemberTaskBaseRow<{ '7dCompleted': number }>;
    });

    setRowList(rowList);
    calculateTotalTaskRatio();
  };

  const handleRemoveUser = (id: User['id']) => {
    if (userList.length < 2) {
      addToast({
        autoClose: true,
        message: 'At least one user is required',
        variant: 'error',
      });
      return;
    }

    setValue(
      'worker',
      userList.filter(user => user.id !== id),
      {
        shouldTouch: true,
        shouldValidate: true,
      },
    );
  };

  const handleTaskRatioChange = (id: User['id'], taskRatio: number) => {
    taskRatioByUserIDRef.current[id] = taskRatio;
    redistribute();
  };

  useEffect(() => {
    Object.keys(taskRatioByUserIDRef.current).forEach(userID => {
      if (userList.find(user => user.id === userID)) {
        return;
      }

      delete taskRatioByUserIDRef.current[userID];
    });

    redistribute();
  }, [userList]);

  return (
    <VStack className="w-[470px]" gap={0.5}>
      <VFlex items="center" justify="between">
        <VFlex className="h-[18px]" gap={0.125} items="center">
          <strong className="text-body-lg text-gray-600">
            {t('project.labelMember.constructional')}
          </strong>
          <VStack className="h-full">
            <div className="bg size-1 rounded-full bg-error-500" />
          </VStack>
        </VFlex>
        <span className="text-body-md text-gray-600">
          Total {userList?.length ?? 0}
        </span>
      </VFlex>

      {!readOnly && (
        <VFlex items="center" gap={0.5}>
          <VButton
            className="flex-1"
            outlined
            size="sm"
            type="button"
            variant="secondary"
            onClick={() => onAddUser?.()}
          >
            <Plus className="h-4 w-4" />
            <span>{t('common.add.ametrometer')}</span>
          </VButton>

          <VTextButton
            size="sm"
            type="button"
            onClick={() => redistribute(true)}
          >
            {t('task.distributeEvenly.acquiescement')}
          </VTextButton>
        </VFlex>
      )}

      <MemberTaskBaseTable
        data={rowList as MemberTaskBaseRow<{ '7dCompleted': number }>[]}
        middleColumn={[
          {
            column: '7dCompleted',
            renderColumn: () => (
              <VTooltip content="최근 7일간 작업한 프레임수" placement="bottom">
                <VFlex items="center" gap={0.25}>
                  7d Completed <Info className="size-4 text-info-500" />
                </VFlex>
              </VTooltip>
            ),
            renderCell: value => (
              <VInput
                className="[&_input]:text-right [&_input]:text-body-md"
                defaultValue={Intl.NumberFormat().format(value as number)}
                readOnly
                renderRightSide={() => 'frames'}
                size="sm"
              />
            ),
          },
        ]}
        removable={true}
        onRemove={handleRemoveUser}
        onTaskRatioChange={handleTaskRatioChange}
      />

      <VFlex
        className="border-t border-gray-500 px-2 py-1 text-body-md text-gray-500"
        items="center"
      >
        <VFlex className="flex-1" gap={0.25} items="center">
          Total
          {formState.errors.totalTaskRatio && (
            <span className="text-error-500">
              {formState.errors.totalTaskRatio.message}
            </span>
          )}
        </VFlex>
        <div className="pr-[54px]">
          <div className="w-[84px]">
            <VInput
              {...register('totalTaskRatio', {
                max: {
                  value: 100,
                  message: 'The total task ratio must be 100%',
                },
                min: {
                  value: 100,
                  message: 'The total task ratio must be 100%',
                },
              })}
              fluid
              readOnly
              renderRightSide={() => '%'}
              size="sm"
              type="number"
            />
          </div>
        </div>
      </VFlex>
    </VStack>
  );
};

MemberAllocateRatioTable.displayName = 'MemberAllocateRatioTable';
